import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { MagnifyingGlass, FolderPlus, ClockCountdown } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProjectRequestSheet({ onCreated }) {
    const [open, setOpen] = useState(false);
    const [years, setYears] = useState([]);
    const [year, setYear] = useState("all");
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [picked, setPicked] = useState(new Set());
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        http.get("/projects/years").then((r) => setYears(r.data));
    }, [open]);

    const load = async () => {
        const params = {};
        if (year !== "all") params.year = year;
        if (q.trim()) params.q = q.trim();
        const r = await http.get("/projects/available", { params });
        setItems(r.data);
    };

    useEffect(() => {
        if (!open) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, year]);

    const toggle = (pid) => {
        setPicked((prev) => {
            const n = new Set(prev);
            if (n.has(pid)) n.delete(pid);
            else n.add(pid);
            return n;
        });
    };

    const submit = async () => {
        if (picked.size === 0) {
            toast.error("Selecciona al menos un proyecto");
            return;
        }
        setBusy(true);
        try {
            const r = await http.post("/assignment-requests", {
                project_ids: Array.from(picked),
                reason: reason || null,
            });
            toast.success(
                `${r.data.count} solicitud${r.data.count === 1 ? "" : "es"} enviada${r.data.count === 1 ? "" : "s"}`,
            );
            setPicked(new Set());
            setReason("");
            setOpen(false);
            onCreated?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al solicitar");
        } finally {
            setBusy(false);
        }
    };

    const visible = useMemo(() => items, [items]);

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button
                    variant="outline"
                    className="rounded-sm"
                    data-testid="request-projects-btn"
                >
                    <FolderPlus size={14} className="mr-2" />
                    Solicitar proyectos
                </Button>
            </SheetTrigger>
            <SheetContent className="rounded-none sm:max-w-2xl w-full flex flex-col">
                <SheetHeader>
                    <SheetTitle className="font-display">
                        Solicitar asignación a proyectos
                    </SheetTitle>
                    <SheetDescription className="text-xs text-slate-500">
                        Marca los proyectos a los que necesitas imputar horas. Un
                        administrador revisará la solicitud.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex gap-2 mt-4">
                    <div className="flex-1 relative">
                        <MagnifyingGlass
                            size={14}
                            className="absolute left-2 top-2.5 text-slate-400"
                        />
                        <Input
                            placeholder="Buscar por nombre o código…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") load();
                            }}
                            className="rounded-sm border-slate-300 pl-7"
                            data-testid="request-search"
                        />
                    </div>
                    <Select value={year} onValueChange={setYear}>
                        <SelectTrigger
                            className="rounded-sm border-slate-300 w-36"
                            data-testid="request-year"
                        >
                            <SelectValue placeholder="Anualidad" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos los años</SelectItem>
                            {years.map((y) => (
                                <SelectItem key={y} value={String(y)}>
                                    {y}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        className="rounded-sm"
                        onClick={load}
                        data-testid="request-search-btn"
                    >
                        Buscar
                    </Button>
                </div>

                <div className="text-xs text-slate-500 mt-3 flex items-center justify-between">
                    <span>
                        {visible.length} proyecto{visible.length === 1 ? "" : "s"}{" "}
                        disponible{visible.length === 1 ? "" : "s"}
                    </span>
                    {picked.size > 0 && (
                        <span className="font-mono text-slate-700">
                            {picked.size} seleccionado{picked.size === 1 ? "" : "s"}
                        </span>
                    )}
                </div>

                <div
                    className="border border-slate-200 mt-2 flex-1 overflow-y-auto"
                    data-testid="request-list"
                >
                    {visible.length === 0 && (
                        <div className="text-center py-12 text-slate-500 text-sm">
                            No hay proyectos disponibles con esos filtros.
                        </div>
                    )}
                    {visible.map((p) => (
                        <label
                            key={p.project_id}
                            className={`flex items-start gap-3 px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${
                                p.pending_request ? "opacity-60" : ""
                            }`}
                            data-testid={`request-item-${p.project_id}`}
                        >
                            <Checkbox
                                checked={picked.has(p.project_id)}
                                disabled={p.pending_request}
                                onCheckedChange={() => toggle(p.project_id)}
                                data-testid={`request-check-${p.project_id}`}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-900 truncate">
                                    {p.name}
                                </div>
                                <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                                    {p.customer_name} ·{" "}
                                    <span className="text-slate-700">
                                        {p.project_code || "—"}
                                    </span>
                                    {p.year && (
                                        <span className="ml-2 inline-block px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold uppercase tracking-wider">
                                            {p.year}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {p.pending_request && (
                                <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-1">
                                    <ClockCountdown size={12} /> Pendiente
                                </span>
                            )}
                        </label>
                    ))}
                </div>

                <div className="mt-3 space-y-2">
                    <Label className="text-xs">Justificación (opcional)</Label>
                    <Textarea
                        rows={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="rounded-sm border-slate-300"
                        placeholder="Por qué necesitas la asignación…"
                        data-testid="request-reason"
                    />
                    <Button
                        onClick={submit}
                        disabled={busy || picked.size === 0}
                        className="w-full rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                        data-testid="request-submit"
                    >
                        Enviar solicitud ({picked.size})
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
